import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    CheckCircleOutlined,
    ErrorOutlined,
    ExpandMoreOutlined,
    RefreshOutlined,
    StorageOutlined,
    CloudOutlined,
    PeopleOutlined,
    BusinessOutlined,
    GitHub,
} from "@mui/icons-material";
import {
    Collapse,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    useGetPlatformHealthQuery,
    useGetOrganizationHealthQuery,
    useGetUserHealthQuery,
} from "state/rtkQuery/health/healthQueries";
import { useGetGitHubStatusQuery } from "state/rtkQuery/githubStatus/githubStatusQueries";

import "./EcrOpsHealthStatus.css";

// ── Service config ────────────────────────────────────────────────────────────

const SERVICES = [
    {
        id: "users",
        name: "User Service",
        icon: <PeopleOutlined />,
        description: "Manages user accounts, permissions, and authentication",
        useQuery: useGetUserHealthQuery,
    },
    {
        id: "organizations",
        name: "Organization Service",
        icon: <BusinessOutlined />,
        description: "Handles organization provisioning, settings, and webhooks",
        useQuery: useGetOrganizationHealthQuery,
    },
    {
        id: "platforms",
        name: "Platform Service",
        icon: <CloudOutlined />,
        description: "Core platform configuration and database management",
        useQuery: useGetPlatformHealthQuery,
    },
];

// ── Component icons ───────────────────────────────────────────────────────────

const COMPONENT_ICONS = {
    "Mongo DB": <StorageOutlined />,
    "RabbitMQ": <StorageOutlined />,
    "Github Api": <CloudOutlined />,
    "Github Webhooks": <CloudOutlined />,
    "Platform Service": <CloudOutlined />,
    "Organization Service": <BusinessOutlined />,
};

// ── Helper functions ──────────────────────────────────────────────────────────

function formatTime(timeStr) {
    // Convert "0:00:00.123456" to "123ms"
    if (!timeStr) return "—";
    const match = timeStr.match(/\.(\d+)/);
    if (match) {
        const micros = parseInt(match[1], 10);
        const ms = Math.round(micros / 1000);
        return `${ms}ms`;
    }
    return timeStr;
}

function getStatusColor(status) {
    const s = (status || "").toLowerCase();
    if (s === "healthy") return { bg: "#e6f9f1", fg: "#86BC25", border: "#86BC25" };
    if (s === "unhealthy" || s === "degraded") return { bg: "#fee", fg: "#dc2626", border: "#dc2626" };
    return { bg: "#f3f4f6", fg: "#6b7280", border: "#d1d5db" };
}

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status = null, size = "medium" }) => {
    const colors = getStatusColor(status);
    const isHealthy = (status || "").toLowerCase() === "healthy";

    return (
        <div
            className={`health-status-badge health-status-badge--${size}`}
            style={{
                background: colors.bg,
                color: colors.fg,
                borderColor: colors.border
            }}
        >
            {isHealthy ? (
                <CheckCircleOutlined fontSize="inherit" />
            ) : (
                <ErrorOutlined fontSize="inherit" />
            )}
            <span>{status || "Unknown"}</span>
        </div>
    );
};

StatusBadge.propTypes = {
    status: PropTypes.string,
    size: PropTypes.oneOf(["small", "medium"]),
};


// ── Component check row ───────────────────────────────────────────────────────

const ComponentCheck = ({ check }) => {
    const colors = getStatusColor(check.status);
    const icon = COMPONENT_ICONS[check.componentType] || <StorageOutlined />;

    return (
        <div className="health-component-check">
            <div className="health-component-check__icon" style={{ color: colors.fg }}>
                {icon}
            </div>
            <div className="health-component-check__body">
                <div className="health-component-check__header">
                    <span className="health-component-check__name">{check.componentType}</span>
                    <StatusBadge status={check.status} size="small" />
                </div>
                <div className="health-component-check__meta">
                    <span>Response Time: {formatTime(check.time)}</span>
                    <span className="health-component-check__divider">•</span>
                    <span className="health-component-check__id">{check.componentId}</span>
                </div>
            </div>
        </div>
    );
};

ComponentCheck.propTypes = {
    check: PropTypes.shape({
        componentId: PropTypes.string.isRequired,
        componentType: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        time: PropTypes.string.isRequired,
    }).isRequired,
};

// ── Service card ──────────────────────────────────────────────────────────────

const ServiceCard = ({ service }) => {
    const [expanded, setExpanded] = useState(false);

    const { data, isLoading, isFetching, isError, refetch } = service.useQuery(undefined, {
        pollingInterval: 30000, // Poll every 30 seconds
    });

    const isHealthy = data?.status?.toLowerCase() === "healthy";
    const statusColors = getStatusColor(data?.status);

    return (
        <div className="health-service-card">
            {/* Header */}
            <div className="health-service-card__header">
                <div className="health-service-card__icon" style={{ color: statusColors.fg }}>
                    {service.icon}
                </div>
                <div className="health-service-card__title-wrap">
                    <h3 className="health-service-card__title">{service.name}</h3>
                    <p className="health-service-card__desc">{service.description}</p>
                </div>
                <div className="health-service-card__actions">
                    <Tooltip title="Refresh">
                        <span>
                        <IconButton
                            size="small"
                            onClick={() => refetch()}
                            disabled={isLoading || isFetching}
                            sx={{ color: "#6b7280" }}
                        >
                            <RefreshOutlined
                                fontSize="small"
                                className={isFetching ? "health-refresh-spin" : ""}
                            />
                        </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* Status overview */}
            <div className="health-service-card__body">
                {isLoading && !data ? (
                    <div className="health-service-card__loading">Loading health data...</div>
                ) : isError ? (
                    <div className="health-service-card__error">
                        <ErrorOutlined /> Failed to fetch health status
                    </div>
                ) : (
                    <>
                        <div className="health-service-card__metrics">
                            <div className="health-metric">
                                <span className="health-metric__label">Status</span>
                                <StatusBadge status={data?.status} />
                            </div>
                            <div className="health-metric">
                                <span className="health-metric__label">Version</span>
                                <span className="health-metric__value">{data?.version || "—"}</span>
                            </div>
                            <div className="health-metric">
                                <span className="health-metric__label">Response Time</span>
                                <span className="health-metric__value">{formatTime(data?.totalTime)}</span>
                            </div>
                            <div className="health-metric">
                                <span className="health-metric__label">Release</span>
                                <span className="health-metric__value">{data?.releaseId || "—"}</span>
                            </div>
                        </div>

                        {/* Expandable component checks */}
                        {data?.checks && data.checks.length > 0 && (
                            <>
                                <button
                                    className="health-service-card__expand-btn"
                                    onClick={() => setExpanded(!expanded)}
                                    type="button"
                                >
                                    <span>Component Checks ({data.checks.length})</span>
                                    <ExpandMoreOutlined
                                        className={expanded ? "health-expand-icon--rotated" : ""}
                                    />
                                </button>

                                <Collapse in={expanded}>
                                    <div className="health-component-checks">
                                        {data.checks.map((check) => (
                                            <ComponentCheck key={check.componentId} check={check} />
                                        ))}
                                    </div>
                                </Collapse>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Auto-refresh indicator */}
            {isFetching && data && (
                <div className="health-service-card__refresh-indicator">
                    <div className="health-refresh-dot" />
                    <span>Refreshing...</span>
                </div>
            )}
        </div>
    );
};

ServiceCard.propTypes = {
    service: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        description: PropTypes.string.isRequired,
        useQuery: PropTypes.func.isRequired,
    }).isRequired,
};

// ── GitHub status card ────────────────────────────────────────────────────────
// GitHub's own platform status (githubstatus.com), a third-party public
// Statuspage API — distinct from the three internal ECR services above.
// Rendered separately rather than folded into SERVICES/ServiceCard because its
// data shape (per-component operational status, real incident narratives) is
// richer than the generic health-check checks[] the other cards render.

// Statuspage component status -> the same color scheme getStatusColor uses.
function getComponentStatusColor(status) {
    if (status === "operational") return { bg: "#e6f9f1", fg: "#86BC25", border: "#86BC25" };
    if (status === "under_maintenance") return { bg: "#eff6ff", fg: "#2563eb", border: "#2563eb" };
    if (status === "degraded_performance" || status === "partial_outage") {
        return { bg: "#fff7ed", fg: "#ea580c", border: "#ea580c" };
    }
    if (status === "major_outage") return { bg: "#fee", fg: "#dc2626", border: "#dc2626" };
    return { bg: "#f3f4f6", fg: "#6b7280", border: "#d1d5db" };
}

function formatComponentStatus(status) {
    return (status || "unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const GitHubStatusCard = () => {
    const [expanded, setExpanded] = useState(false);

    const { data, isLoading, isFetching, isError, refetch } = useGetGitHubStatusQuery(undefined, {
        pollingInterval: 60000, // GitHub's status is CDN-cached for 10s; 60s polling is more than sufficient.
    });

    const isHealthy = data?.status === "healthy";
    const statusColors = isHealthy
        ? { bg: "#e6f9f1", fg: "#86BC25", border: "#86BC25" }
        : { bg: "#fee", fg: "#dc2626", border: "#dc2626" };
    const activeIncidents = data?.incidents ?? [];
    // Only components meant to be shown standalone (excludes e.g. the
    // "Copilot AI Model Providers" informational link entry Statuspage returns).
    const visibleComponents = (data?.components ?? []).filter((c) => c.showcase !== false);

    return (
        <div className="health-service-card">
            {/* Header */}
            <div className="health-service-card__header">
                <div className="health-service-card__icon" style={{ color: statusColors.fg }}>
                    <GitHub />
                </div>
                <div className="health-service-card__title-wrap">
                    <h3 className="health-service-card__title">GitHub</h3>
                    <p className="health-service-card__desc">
                        Live platform status from githubstatus.com — Git operations, Actions, Packages, and more
                    </p>
                </div>
                <div className="health-service-card__actions">
                    <Tooltip title="Refresh">
                        <span>
                        <IconButton
                            size="small"
                            onClick={() => refetch()}
                            disabled={isLoading || isFetching}
                            sx={{ color: "#6b7280" }}
                        >
                            <RefreshOutlined
                                fontSize="small"
                                className={isFetching ? "health-refresh-spin" : ""}
                            />
                        </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* Status overview */}
            <div className="health-service-card__body">
                {isLoading && !data ? (
                    <div className="health-service-card__loading">Loading GitHub status...</div>
                ) : isError ? (
                    <div className="health-service-card__error">
                        <ErrorOutlined /> Failed to fetch GitHub status
                    </div>
                ) : (
                    <>
                        <div className="health-service-card__metrics">
                            <div className="health-metric">
                                <span className="health-metric__label">Status</span>
                                <div
                                    className="health-status-badge"
                                    style={{ background: statusColors.bg, color: statusColors.fg, borderColor: statusColors.border }}
                                >
                                    {isHealthy ? <CheckCircleOutlined fontSize="inherit" /> : <ErrorOutlined fontSize="inherit" />}
                                    <span>{data?.description}</span>
                                </div>
                            </div>
                            <div className="health-metric">
                                <span className="health-metric__label">Active Incidents</span>
                                <span className="health-metric__value">{activeIncidents.length}</span>
                            </div>
                        </div>

                        {/* Active incidents — shown up front, not tucked in the collapsible section,
                            since an unresolved incident is the most actionable information here. */}
                        {activeIncidents.length > 0 && (
                            <div className="health-component-checks" style={{ marginBottom: 16 }}>
                                {activeIncidents.map((incident) => (
                                    <div key={incident.id} className="health-component-check">
                                        <div className="health-component-check__icon" style={{ color: "#dc2626" }}>
                                            <ErrorOutlined />
                                        </div>
                                        <div className="health-component-check__body">
                                            <div className="health-component-check__header">
                                                <span className="health-component-check__name">{incident.name}</span>
                                                <span className="health-status-badge health-status-badge--small" style={{ background: "#fee", color: "#dc2626", borderColor: "#dc2626" }}>
                                                    {formatComponentStatus(incident.status)}
                                                </span>
                                            </div>
                                            <div className="health-component-check__meta">
                                                <span>Impact: {formatComponentStatus(incident.impact)}</span>
                                                <span className="health-component-check__divider">•</span>
                                                <a href={incident.shortlink} target="_blank" rel="noreferrer noopener">
                                                    View on GitHub Status
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Expandable per-component status */}
                        {visibleComponents.length > 0 && (
                            <>
                                <button
                                    className="health-service-card__expand-btn"
                                    onClick={() => setExpanded(!expanded)}
                                    type="button"
                                >
                                    <span>Components ({visibleComponents.length})</span>
                                    <ExpandMoreOutlined
                                        className={expanded ? "health-expand-icon--rotated" : ""}
                                    />
                                </button>

                                <Collapse in={expanded}>
                                    <div className="health-component-pill-grid">
                                        {visibleComponents.map((component) => {
                                            const colors = getComponentStatusColor(component.status);
                                            return (
                                                <Tooltip key={component.id} title={formatComponentStatus(component.status)}>
                                                    <div
                                                        className="health-component-pill"
                                                        style={{ background: colors.bg, color: colors.fg, borderColor: colors.border }}
                                                    >
                                                        <span className="health-component-pill__dot" style={{ background: colors.fg }} />
                                                        <span className="health-component-pill__name">{component.name}</span>
                                                    </div>
                                                </Tooltip>
                                            );
                                        })}
                                    </div>
                                </Collapse>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Auto-refresh indicator */}
            {isFetching && data && (
                <div className="health-service-card__refresh-indicator">
                    <div className="health-refresh-dot" />
                    <span>Refreshing...</span>
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const EcrOpsHealthStatus = () => {
    const now = new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    return (
        <div>
            {/* Page header */}
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">Operations · Monitoring</p>
                <h1 className="ecr-ops-page-header__title">System Health</h1>
                <p className="ecr-ops-page-header__sub">
                    Real-time health monitoring for all ECR platform services. Status updates every 30 seconds.
                </p>
            </div>

            <div className="ecr-ops-content">
                {/* Info banner */}
                <div className="health-info-banner">
                    <div className="health-info-banner__icon">
                        <CheckCircleOutlined />
                    </div>
                    <div className="health-info-banner__text">
                        <strong>All Systems Operational</strong>
                        <span>Last updated: {now}</span>
                    </div>
                </div>

                {/* Service cards */}
                <div className="health-service-grid">
                    {SERVICES.map((service) => (
                        <ServiceCard key={service.id} service={service} />
                    ))}
                    <GitHubStatusCard />
                </div>
            </div>
        </div>
    );
};

export default EcrOpsHealthStatus;
